import {
  Body1Strong,
  Button,
  Divider,
  Image,
  MessageBarIntent,
  Title1
} from "@fluentui/react-components";
import { ArrowResetFilled, ArrowResetRegular, bundleIcon } from "@fluentui/react-icons";
import * as React from "react";
import { useEffect, useState } from "react";
import { FormulaNameStr } from "src/taskpane/taskpane";
import { RecResult } from "../../../client";
import { ValidationStateStr } from "../Inputs/BaseControlledInput";
import { default as UserInput, default as UserInputData, ValidatorFunctionType } from "../Inputs/UserInput";
import Intent from "../MessageBar/Intent";

const ArrowReset = bundleIcon(ArrowResetFilled, ArrowResetRegular);

type UserInputData = {
  labelDescription: string;
  label: string;
  isRequired: boolean;
  validatorFunction?: ValidatorFunctionType;
  updateValue?: CallableFunction & {
    (label: string, value: string): void;
  };
  options?: string[];
  isButtonHelper?: boolean;
  buttonHelperType?: "sum" | "rangePicker";
};

export type ResultObject = {
  // [key: string]: string | number | boolean;
  intent?: MessageBarIntent;
  title?: string;
  message?: string;
  result?: string | number | boolean | RecResult[];
};

interface MyFormProps {
  title: string;
  description: string;
  inputs: UserInputData[];
  onSubmit: (values: { [label: string]: string }) => Promise<ResultObject>;
  actionElement: React.ComponentType<{ result: string | number | boolean | RecResult[]; isDisabled: boolean }>;
  startIntentTitle: string;
  startIntentMessage: string;
}

type UserInputValue = {
  [label: string]: {
    value: string | FormulaNameStr;
    isValid: boolean;
    message: string;
    validationState: ValidationStateStr;
  };
};

const MyForm: React.FC<MyFormProps> = (props: MyFormProps) => {
  const [values, setValues] = useState<UserInputValue>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<ResultObject>({
    intent: "info",
    title: "Title",
    message: "Message",
    result: "Result",
  });
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);
  const [isActionDisabled, setIsActionDisabled] = useState<boolean>(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const [intent, setIntent] = useState<MessageBarIntent>("info");
  const [intentTitle, setIntentTitle] = useState<string>(props.startIntentTitle);
  const [intentMessage, setIntentMessage] = useState<string>(props.startIntentMessage);

  const updateValue = (
    label: string,
    value: string,
    isValid: boolean,
    message: string,
    validationState: ValidationStateStr
  ) => {
    const newValues = {
      ...values,
      [label]: { value, isValid, message, validationState },
    };
    setValues(newValues);
  };

  const isFormValid = Object.values(values).every((field) => field.isValid);

  useEffect(() => {
    setIsSubmitDisabled(!isFormValid);
  }, [isFormValid]);

  const resetForm = () => {
    const resetValues: UserInputValue = {};

    props.inputs.forEach((input) => {
      resetValues[input.label] = {
        value: "",
        isValid: !input.isRequired,
        message: "",
        validationState: "none",
      };
    });

    setValues(resetValues);
    setIsFormDisabled(false);
    setIsActionDisabled(true);
    setIsSubmitDisabled(true);
    setResult({
      intent: "info",
      title: "Form Reset",
      message: "The form has been reset.",
    });
    setIntent("info");
    setIntentTitle(props.startIntentTitle);
    setIntentMessage(props.startIntentMessage);
  };

  useEffect(() => {
    // Initialize values state with empty strings for each input label
    const initialValues: UserInputValue = {};
    props.inputs.forEach((input) => {
      initialValues[input.label] = {
        value: "",
        isValid: !input.isRequired, // If not required, it's valid by default
        message: "",
        validationState: "none",
      };
    });
    setValues(initialValues);
  }, [props.inputs]);

  const reduceValuesToFormData = (values: UserInputValue) => {
    const formData: { [label: string]: string } = {};
    for (const label in values) {
      formData[label] = values[label].value;
    }
    return formData;
  };

  const handleSubmit = async () => {
    const formData = reduceValuesToFormData(values);
    setIsLoading(true);
    try {
      const res = await props.onSubmit(formData);
      setResult(res);
      setIsActionDisabled(false);
      setIsSubmitDisabled(true);
      setIsFormDisabled(true);
      setIntent(res.intent);
      setIntentTitle(res.title);
      setIntentMessage(res.message);
    } catch (e) {
      setResult({
        intent: "error",
        title: "Error",
        message: String(e),
      });
      setIsActionDisabled(true);
      setIntent("error");
      setIntentTitle("Error");
      setIntentMessage(String(e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* // <Card> */}
      <Image src="/assets/icon-64.png" shape="circular"></Image>
      <Title1 align="center">{props.title}</Title1>
      <Body1Strong align="justify">{props.description}</Body1Strong>
      {props.inputs.map((input, index) => (
        <UserInput
          key={index}
          labelDescription={input.labelDescription}
          label={input.label}
          isRequired={input.isRequired}
          validatorFunction={input.validatorFunction}
          updateValue={updateValue}
          value={values[input.label]?.value || ""}
          message={values[input.label]?.message || ""}
          validationState={values[input.label]?.validationState || "none"}
          options={input.options ? input.options : undefined}
          disabled={isLoading || isFormDisabled}
          isButtonHelper={input.isButtonHelper || false}
          buttonHelperType={
            input.isButtonHelper ? (input.label as "sum" | "rangePicker") : undefined
          }
        />
      ))}
      <Divider></Divider>
      <Button disabled={!isFormValid || isLoading || isSubmitDisabled} onClick={handleSubmit}>
        {isLoading ? "Loading..." : "Submit"}
      </Button>
      <Intent intent={intent} title={intentTitle} message={intentMessage}></Intent>
      <Divider></Divider>
      {<props.actionElement result={result.result} isDisabled={isActionDisabled || intent !== "success"} />}
      <Divider></Divider>
      <Divider></Divider>
      <Button icon={<ArrowReset />} onClick={resetForm}>Reset</Button>
      {/* // </Card> */}
    </>
  );
};

export default MyForm;
